import { z } from 'zod';
import { getSlug } from '@provider-directory/functions/get-slug';
import { SearchTermTypeaheadSchema } from '@provider-directory/types/searchterm.zod';

export const TypeaheadCategories = z.enum(['providerNames', 'specialties']);
export type TypeaheadCategory = z.infer<typeof TypeaheadCategories>;

export interface TypeAheadRequest {
  terms: string;
  categories?: TypeaheadCategory[];
}

export const typeaheadNameSchema = z.object({
  first: z.string().optional(),
  middle: z.string().optional(),
  last: z.string().optional(),
  full: z.string(),
});

export const providerNameTypeaheadItemSchema = z.object({
  npi: z.string(),
  value: z.string(),
  name: typeaheadNameSchema,
  slug: z.string(),
  degrees: z.array(z.string()).optional(),
  specialties: z.array(z.string()).optional(),
  professionalTitles: z.array(z.string()).optional(),
  photo: z.string().optional(),
});
type ProviderNameTypeaheadItem = z.infer<
  typeof providerNameTypeaheadItemSchema
>;

export const specialtiesTypeaheadItemSchema = z.object({
  value: z.string().optional(),
  name: z.string().optional(),
  slug: z.string(),
  type: z.enum(['specialty', 'synonym']),
});
type SpecialtiesTypeaheadItem = z.infer<typeof specialtiesTypeaheadItemSchema>;

export type TypeaheadItem =
  | ProviderNameTypeaheadItem
  | SpecialtiesTypeaheadItem;

export const typeaheadResponseSchema = z.object({
  providerNames: z.array(providerNameTypeaheadItemSchema).optional(),
  specialties: z.array(specialtiesTypeaheadItemSchema).optional(),
});

export const typeaheadResponseV2Schema = z.object({
  providerNames: z.array(providerNameTypeaheadItemSchema).optional(),
  searchTerms: z.array(SearchTermTypeaheadSchema).optional(),
});

export const typeaheadV2ToV1Response = z.codec(
  typeaheadResponseV2Schema,
  typeaheadResponseSchema,
  {
    decode: ({ providerNames, searchTerms }) => ({
      providerNames,
      specialties: searchTerms?.map(
        (term) =>
          ({
            value: term.name,
            name: term.name,
            slug: getSlug(term.name),
            type: term.type === 'specialty' ? 'specialty' : 'synonym',
          }) as SpecialtiesTypeaheadItem,
      ),
    }),
    encode: (v1Response) => ({
      providerNames: v1Response.providerNames,
      searchTerms: v1Response.specialties?.map(
        (item) =>
          ({
            id: item.slug,
            name: item.name ?? '',
            type: item.type === 'specialty' ? 'specialty' : 'synonym',
          }) as z.infer<typeof SearchTermTypeaheadSchema>,
      ),
    }),
  },
);

export type TypeaheadResponse = z.infer<typeof typeaheadResponseSchema>;
export type TypeaheadResponseV2 = z.infer<typeof typeaheadResponseV2Schema>;
